import { CSS, globalCss, styled as stitches } from '@stitches/react';
import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { LauncherWindowData, useLauncherWindowStore } from '../../hooks/stores/launcherWindowStore';
import { SessionData, useSessionStore } from '../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';
import { AbsolutePositionGuides } from '../views/AbsolutePositionGuides';
import { RelativePositionGuides } from '../views/RelativePositionGuides';
import { SnappingGrid } from '../views/SnappingGrid';
import { SnappingGuideLines } from '../views/SnappingGuideLines';
import { EditingTextarea } from './EditingTextarea';
import { ElementCursors } from './ElementCursors';
import { LauncherElement } from './LauncherElement';
import { ElementHandle } from './elementHandle/ElementHandle';
import { useScene } from '../../context/sceneContext';

export const LauncherWindow = () => {
  const [sessionData] = useSessionStore<SessionData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [UIData] = useUIStore<UIData>();

  const globalStyles = useMemo(
    () =>
      globalCss({
        '@import': launcherWindowData.fonts?.map(({ src }) => src),
      }),
    [launcherWindowData.fonts]
  );

  const Window = useMemo(() => stitches('div', launcherWindowData.class as CSS), [launcherWindowData]);
  const TextArea = useMemo(() => () => <EditingTextarea />, []);

  globalStyles();
  
  return (
    <>
      <ZoomContainer>
        <InnerContainer
          id="launcherWindow"
          ui-element="launcherWindow"
          style={{
            translate: `${UIData.sceneOffset.x}px ${UIData.sceneOffset.y}px`,
            transform: `scale(${UIData.sceneZoom})`,
            borderRadius: (launcherWindowData.class as CSSStyleDeclaration)?.borderRadius,
          }}
          $isLoaded={sessionData.isLoaded}
        >
          <Window
            style={{
              width: launcherWindowData.windowWidth,
              height: launcherWindowData.windowHeight,
            }}
          >
            {launcherWindowData.children?.map((element) => <LauncherElement key={element.id} data={element} />)}
          </Window>
          {UIData.selectedElementIDs.length > 1 && (
            <ElementCursors $isElementSelected={true} data={UIData.selectedElementIDs} />
          )}
          <ElementCursors data={UIData.hoveredLauncherElementIDs} />
          <ElementHandle />
          {UIData.elementInEditingModeID && <TextArea />}
          <SnappingGrid />
          <SnappingGuideLines />
          <AbsolutePositionGuides />
          <RelativePositionGuides />
        </InnerContainer>
      </ZoomContainer>
    </>
  );
};

// This component is isolated to not use useScene directly and prevent rerenders breaking element transitions
const ZoomContainer = ({ children }: { children: ReactNode }) => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  
  return (
    <Container
      id="scene"
      onWheel={(e) => {
        const target = e.target as HTMLElement;
        const isOverLauncherWindow = target.closest('#launcherWindow') !== null;
        
        // Don't zoom if we're in simulation mode and over the launcher window
        if (!(UIData.editorMode === 'simulation' && isOverLauncherWindow)) {
          if (e.deltaY > 0) {
            scene.zoomOut();
          } else {
            scene.zoomIn();
          }
        }
      }}
    >
      {children}
    </Container>
  );
};
const Container = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-right: 300px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const InnerContainer = styled.div<{ $isDragging?: boolean, $isLoaded: boolean }>`
  position: absolute;
  box-shadow: var(--shadow-1);
  transition: opacity 0.3s;

  ${({ $isLoaded }) => !$isLoaded && 'opacity: 0.5; filter: grayscale(100%) blur(2px); pointer-events: none;'}
`;

